import { isNativeApp } from "@/lib/nativeBridge";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { thumbUrl } from "@/lib/utils-app";
import { DogIcon as Dog, ForkKnifeIcon as ForkKnife, PersonSimpleWalkIcon as PersonSimpleWalk, DropIcon as Drop, HeartIcon as Heart, PillIcon as Pill, WarningIcon as Warning, ArrowRightIcon as ArrowRight, FlameIcon as Flame, CalendarBlankIcon as CalendarBlank, SparkleIcon as Sparkle } from "@phosphor-icons/react";
import { LuxuryCard, Indicium } from "@/components/luxury";
import { Button } from "@/components/ui/button";

/**
 * "Today with your pack" — Dog Log dashboard widget.
 *
 * Renders a single LuxuryCard listing every dog the user owns with
 * today's care-log rollup as inline pills: meals, walks, potty, play,
 * meds, plus the current walk streak and a "Vet?" warning chip when
 * symptoms have repeated severity ≥3 for 2+ days.
 *
 * Visibility rules:
 *   • If the user has **no Care+ access** AND **no dogs**, the widget is
 *     hidden completely (avoids polluting buyer-only dashboards).
 *   • If the user has dogs but **no Care+**, we show a slim upgrade
 *     nudge ("Unlock Dog Log to track meals, walks, weight & symptoms")
 *     instead of the per-dog rows.
 *   • If Care+ is active but the user owns no dogs, we hide the widget
 *     entirely (nothing to track yet).
 *   • Otherwise: one row per dog with a Quick-log link to `/dogs/<id>`
 *     where the Care tab lives.
 *
 * The backend endpoint (`/api/care/today`) returns a soft response on
 * 401/402 — `active=false, dogs=[]` — so a single fetch handles every
 * state without extra round-trips.
 */
const KIND_META = {
  meal: { icon: ForkKnife, label: "meal" },
  walk: { icon: PersonSimpleWalk, label: "walk" },
  potty: { icon: Drop, label: "potty" },
  play: { icon: Heart, label: "play" },
  meds: { icon: Pill, label: "med" },
};

function pluralize(n, word) {
  return `${n} ${word}${n === 1 ? "" : "s"}`;
}

function timeAgo(iso) {
  if (!iso) return null;
  try {
    const then = new Date(iso).getTime();
    const mins = Math.round((Date.now() - then) / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.round(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.round(hrs / 24);
    return `${days}d ago`;
  } catch {
    return null;
  }
}

function DogRow({ dog }) {
  const counts = dog.today_counts || {};
  const pills = Object.entries(KIND_META)
    .filter(([k]) => counts[k] > 0)
    .map(([k, meta]) => ({ kind: k, count: counts[k], ...meta }));
  const lastWalkAgo = timeAgo(dog.last_walk_at);
  const lastMealAgo = timeAgo(dog.last_meal_at);

  return (
    <Link
      to={`/dogs/${dog.id}?tab=care`}
      className="flex items-center gap-4 py-3.5 -mx-2 px-2 rounded-lg hover:bg-cream/50 transition-colors"
      data-testid={`today-dog-${dog.id}`}
    >
      <div className="h-14 w-14 rounded-xl bg-cream overflow-hidden flex-shrink-0 ring-1 ring-[#E2DAC2]">
        {dog.photo ? (
          <img src={thumbUrl(dog.photo)} alt={dog.name} className="h-full w-full object-cover photo-luxury" />
        ) : (
          <div className="h-full w-full flex items-center justify-center">
            <Dog size={24} className="text-[#6B6B6B]" />
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-spectral text-lg tracking-tight truncate">{dog.name}</span>
          {dog.walk_streak_days > 1 && (
            <span
              className="inline-flex items-center gap-1 text-[10px] font-bold tracking-[0.12em] uppercase text-gold-dark bg-[#FBF7EC] rounded-full px-2 py-0.5 border border-[#E8E0CC]"
              data-testid={`today-dog-${dog.id}-streak`}
              title={`${dog.walk_streak_days}-day walk streak`}
            >
              <Flame size={11} weight="fill" /> {dog.walk_streak_days}d streak
            </span>
          )}
          {dog.upcoming_appts_7d > 0 && (
            <span
              className="inline-flex items-center gap-1 text-[10px] font-bold tracking-[0.12em] uppercase text-ink bg-white rounded-full px-2 py-0.5 border border-[#E5E5E5]"
              title={`${dog.upcoming_appts_7d} appointment(s) in the next 7 days`}
            >
              <CalendarBlank size={11} weight="duotone" /> {dog.upcoming_appts_7d} appt
            </span>
          )}
          {dog.consider_vet && (
            <span
              className="inline-flex items-center gap-1 text-[10px] font-bold tracking-[0.12em] uppercase text-crimson bg-crimson/10 rounded-full px-2 py-0.5 border border-crimson/30"
              data-testid={`today-dog-${dog.id}-vet`}
              title="Symptoms repeated at severity 3+ for 2+ days"
            >
              <Warning size={11} weight="fill" /> Consider vet
            </span>
          )}
        </div>
        {pills.length > 0 ? (
          <div className="mt-1.5 flex items-center gap-2.5 flex-wrap text-[12px] text-[#3a3a3a]">
            {pills.map((p) => (
              <span key={p.kind} className="inline-flex items-center gap-1 font-tabular">
                <p.icon size={13} weight="duotone" className="text-gold-dark" />
                {pluralize(p.count, p.label)}
              </span>
            ))}
            {(lastWalkAgo || lastMealAgo) && (
              <span className="text-[11px] text-[#6B6B6B]">
                {lastWalkAgo && <>last walk {lastWalkAgo}</>}
                {lastWalkAgo && lastMealAgo && " · "}
                {lastMealAgo && <>last meal {lastMealAgo}</>}
              </span>
            )}
          </div>
        ) : (
          <div className="mt-1 text-[12.5px] text-[#6B6B6B] italic">
            Nothing logged today — tap to start.
          </div>
        )}
      </div>
      <ArrowRight size={14} weight="bold" className="text-[#6B6B6B] flex-shrink-0" />
    </Link>
  );
}

function UpgradeNudge() {
  return (
    <div
      className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-xl bg-[#FBF7EC] border border-[#E8E0CC] px-4 py-3"
      data-testid="today-dog-log-upgrade-nudge"
    >
      <div className="flex items-start gap-3">
        <Sparkle size={18} weight="fill" className="text-gold-dark mt-0.5 flex-shrink-0" />
        <div>
          <p className="text-sm font-medium text-ink leading-snug">
            Unlock Dog Log to track meals, walks, weight, meds & symptoms.
          </p>
          {!isNativeApp() && (
          <p className="text-[12px] text-[#6B6B6B] mt-0.5">
            $9.99/mo or $69/yr — free with Breeder Pro & Kennel Elite.
          </p>
          )}
        </div>
      </div>
      <Link to="/dog-log" className="flex-shrink-0">
        <Button
          size="sm"
          className="rounded-full bg-ink hover:bg-crimson text-white text-[12px] h-9 px-4"
          data-testid="today-dog-log-upgrade-btn"
        >
          Explore Dog Log
        </Button>
      </Link>
    </div>
  );
}

export default function TodayWithDogsWidget() {
  const [data, setData] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [hasDogs, setHasDogs] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get("/care/today")
      .then((r) => {
        if (cancelled) return;
        setData(r.data || { active: false, dogs: [] });
      })
      .catch(() => {
        if (cancelled) return;
        setData({ active: false, dogs: [] });
      })
      .finally(() => { if (!cancelled) setLoaded(true); });
    // Independently check if the user owns any dogs so we can offer the
    // upgrade nudge when they have dogs but no Care+ subscription.
    api.get("/dogs?limit=1")
      .then((r) => {
        if (cancelled) return;
        const rows = Array.isArray(r.data) ? r.data : (r.data?.dogs || []);
        setHasDogs(rows.length > 0);
      })
      .catch(() => { if (!cancelled) setHasDogs(false); });
    return () => { cancelled = true; };
  }, []);

  if (!loaded) return null;

  const active = !!data?.active;
  const dogs = data?.dogs || [];

  // Care+ active but user owns no dogs → hide entirely (nothing to track).
  if (active && dogs.length === 0) return null;

  // Not Care+, no dogs → hide (likely a buyer-only persona).
  if (!active && !hasDogs) return null;

  return (
    <LuxuryCard className="p-6 mb-8" data-testid="today-with-dogs-widget">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-spectral text-2xl tracking-tight flex items-center gap-3">
          <Indicium icon={Heart} tone="gold" size="sm" />
          Today with your pack
        </h2>
        <Link
          to="/dog-log"
          className="text-sm text-ink hover:text-crimson transition-colors"
          data-testid="today-dog-log-link"
        >
          About Dog Log
        </Link>
      </div>
      <div className="gold-hairline mb-2" />
      {active ? (
        <div className="divide-y divide-[#E5E5E5]">
          {dogs.map((dog) => (
            <DogRow key={dog.id} dog={dog} />
          ))}
        </div>
      ) : (
        <UpgradeNudge />
      )}
    </LuxuryCard>
  );
}
